<template>
    <div class="space-y-3">

        <!-- Particulier / Pro toggle -->
        <div v-if="!forcePro" class="inline-flex bg-gray-100 rounded-lg p-0.5">
            <button
                v-for="opt in typeOptions" :key="opt.value"
                @click="client.type_client = opt.value"
                class="px-4 py-1.5 rounded-md text-xs font-semibold transition-all"
                :class="client.type_client === opt.value
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'text-gray-400 hover:text-gray-600'"
            >{{ opt.label }}</button>
        </div>

        <!-- Particulier : Nom + Prénom -->
        <div v-if="client.type_client === 'particulier'" class="grid grid-cols-2 gap-2">
            <div>
                <label class="text-xs text-gray-400 block mb-1">Nom <span class="text-red-400">*</span></label>
                <input v-model="client.nom" type="text" placeholder="Dupont" autocomplete="off"
                    class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 transition-colors bg-white"
                    @input="onFieldInput('nom', $event.target.value)" />
            </div>
            <div>
                <label class="text-xs text-gray-400 block mb-1">Prénom</label>
                <input v-model="client.prenom" type="text" placeholder="Jean" autocomplete="off"
                    class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 transition-colors bg-white"
                    @input="onFieldInput('prenom', $event.target.value)" />
            </div>
        </div>

        <!-- Pro : Société + SIRET -->
        <div v-else class="space-y-2">
            <div>
                <label class="text-xs text-gray-400 block mb-1">Société <span class="text-red-400">*</span></label>
                <input v-model="client.nom" type="text" placeholder="Exemple SARL" autocomplete="off"
                    class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 transition-colors bg-white"
                    @input="onFieldInput('nom', $event.target.value)" />
            </div>
            <div>
                <label class="text-xs text-gray-400 block mb-1">SIRET</label>
                <input :value="client.siret" @input="onSiretInput" @keydown="onSiretKeydown"
                    type="text" inputmode="numeric" placeholder="12345678900012" maxlength="14"
                    class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 transition-colors bg-white font-mono tracking-wide"
                    :class="client.siret && client.siret.length === 14 ? 'border-green-300' : ''" />
            </div>
        </div>

        <!-- Téléphone + Email -->
        <div class="grid grid-cols-2 gap-2">
            <div>
                <label class="text-xs text-gray-400 block mb-1">Téléphone</label>
                <input :value="phoneDisplay"
                    @input="onPhoneFieldInput($event)"
                    @keydown="onPhoneKeydown"
                    type="tel" inputmode="numeric" placeholder="06 12 34 56 78" autocomplete="off"
                    class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 transition-colors bg-white"
                    :class="phoneValid ? 'border-green-300' : ''" />
            </div>
            <div>
                <label class="text-xs text-gray-400 block mb-1">Email</label>
                <input v-model="client.email" type="email" placeholder="jean@mail.com" autocomplete="off"
                    class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 transition-colors bg-white"
                    @input="onFieldInput('email', $event.target.value)" />
            </div>
        </div>

        <!-- Clients existants -->
        <div v-if="suggestions.length > 0">
            <p class="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">
                <i class="fas fa-users mr-1.5"></i>Clients existants
            </p>
            <div class="space-y-1.5 overflow-y-auto pr-0.5" style="max-height: 240px">
                <button
                    v-for="s in suggestions" :key="s.id"
                    @click="selectSuggestion(s)"
                    class="w-full flex items-center gap-3 p-3 rounded-xl border-2 border-gray-100 bg-white hover:border-orange-300 hover:bg-orange-50 transition-all text-left group"
                >
                    <!-- Avatar -->
                    <div class="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0 font-bold text-orange-500 text-sm group-hover:bg-orange-200 transition-colors">
                        {{ initials(s) }}
                    </div>
                    <!-- Info -->
                    <div class="flex-1 min-w-0">
                        <p class="text-sm font-bold text-gray-800 truncate capitalize">
                            {{ [s.prenom, s.nom].filter(Boolean).join(' ') }}
                        </p>
                        <div class="flex items-center gap-2 mt-0.5 flex-wrap">
                            <span v-if="s.telephone" class="text-xs text-gray-400 flex items-center gap-1">
                                <i class="fas fa-phone text-gray-300" style="font-size:9px"></i>{{ s.telephone }}
                            </span>
                            <span v-if="s.email" class="text-xs text-gray-400 truncate flex items-center gap-1">
                                <i class="fas fa-envelope text-gray-300" style="font-size:9px"></i>{{ s.email }}
                            </span>
                        </div>
                    </div>
                    <!-- CTA -->
                    <div class="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <span class="text-xs font-bold text-orange-500 flex items-center gap-1">
                            Sélectionner <i class="fas fa-arrow-right text-xs"></i>
                        </span>
                    </div>
                </button>
            </div>
        </div>

    </div>
</template>

<script>
function emptyClient(overrides = {}) {
    return {
        type:            '',
        type_client:     'particulier',
        nom:             '',
        prenom:          '',
        siret:           '',
        telephone:       '',
        email:           '',
        regie_id:        0,
        proprietaire_id: 0,
        ...overrides,
    }
}

export default {
    name: 'ClientBlock',

    props: {
        value:    { type: Object,  default: () => ({}) },
        forcePro: { type: Boolean, default: false },
        title:    { type: String,  default: '' },
        icon:     { type: String,  default: '' },
        idPrefix: { type: String,  default: 'cb' },
    },

    data() {
        return {
            client:     { ...emptyClient(), ...this.value },
            rawDigits:  '',
            typeOptions: [
                { value: 'particulier',   label: 'Particulier' },
                { value: 'professionnel', label: 'Pro' },
            ],
            suggestions:    [],
            _searchTimeout: null,
        }
    },

    computed: {
        phoneDisplay() {
            return this.rawDigits.replace(/(\d{2})(?=\d)/g, '$1 ').trim()
        },
        phoneValid() {
            return this.rawDigits.length === 10
        },
    },

    watch: {
        value: {
            deep: true,
            handler(v) {
                if (JSON.stringify(v) !== JSON.stringify(this.client)) {
                    this.client = { ...emptyClient(), ...v }
                    this.parsePhone(v.telephone)
                }
            },
        },
        client: {
            deep: true,
            handler(v) {
                if (JSON.stringify(v) !== JSON.stringify(this.value)) {
                    this.$emit('input', { ...v })
                }
            },
        },
        rawDigits(val) {
            this.client.telephone = val
        },
    },

    mounted() {
        if (this.forcePro) this.client.type_client = 'professionnel'
        this.parsePhone(this.client.telephone)
    },

    beforeDestroy() {
        clearTimeout(this._searchTimeout)
    },

    methods: {
        /* ── Phone ──────────────────────────────────────── */
        parsePhone(tel) {
            this.rawDigits = tel ? tel.replace(/\D/g, '').slice(0, 10) : ''
        },
        onPhoneFieldInput(e) {
            this.rawDigits = e.target.value.replace(/\D/g, '').slice(0, 10)
            this.onFieldInput('telephone', this.rawDigits)
        },
        onPhoneKeydown(e) {
            const ctrl = ['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Home','End']
            if (ctrl.includes(e.key) || e.ctrlKey || e.metaKey) return
            if (!/^\d$/.test(e.key)) { e.preventDefault(); return }
            if (this.rawDigits.length >= 10) e.preventDefault()
        },

        /* ── SIRET ──────────────────────────────────────── */
        onSiretInput(e) {
            this.client.siret = e.target.value.replace(/\D/g, '').slice(0, 14)
        },
        onSiretKeydown(e) {
            const ctrl = ['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Home','End']
            if (ctrl.includes(e.key) || e.ctrlKey || e.metaKey) return
            if (!/^\d$/.test(e.key)) { e.preventDefault(); return }
            if (this.client.siret?.length >= 14) e.preventDefault()
        },

        /* ── Autocomplete ───────────────────────────────── */
        onFieldInput(field, value) {
            clearTimeout(this._searchTimeout)
            const minLen = field === 'telephone' ? 4 : 2
            if (!value || value.length < minLen) { this.suggestions = []; return }
            this._searchTimeout = setTimeout(() => this.fetchSuggestions(field, value), 350)
        },

        async fetchSuggestions(field, value) {
            try {
                const res = await this.$request.post('/clients/search-autocomplete', {
                    key: field,
                    value: value,
                })
                this.suggestions = res ?? []
            } catch(e) {
                console.error('Autocomplete error:', e)
                this.suggestions = []
            }
        },

        selectSuggestion(s) {
            this.client = {
                ...this.client,
                nom:         s.nom         || this.client.nom,
                prenom:      s.prenom      || this.client.prenom,
                email:       s.email       || this.client.email,
                siret:       s.siret       || this.client.siret,
                type_client: s.type_client || this.client.type_client,
                client_id:    s.id  || 0,
            }
            if (s.telephone) this.parsePhone(s.telephone)
            this.$emit('client-selected', s)
            this.suggestions = []
        },

        initials(s) {
            return ((s.prenom || '').charAt(0) + (s.nom || '').charAt(0)).toUpperCase() || '?'
        },
    },
}
</script>